import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import CaseStudyService, { CaseResponse, CaseStudy } from "../../_services/CaseStudyService";

interface Props {
  stepId: number | null;
  batchId: number | null;
  initialCaseId: number | null;
}

function formatDate(v: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const evaluated = status === "evaluated";
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "2px 10px",
        borderRadius: 999,
        background: evaluated ? "#ecfdf5" : "#eef2ff",
        color: evaluated ? "#047857" : "#4338ca",
      }}
    >
      {evaluated ? "Evaluated" : "Submitted"}
    </span>
  );
};

/**
 * Teacher view for correcting self-paced case-study responses. Pick a case
 * (scoped to the incoming step when opened from the class-review deep-link, else
 * the whole library), see who has responded, open one, and record feedback +
 * an optional score.
 */
const CaseResponsesPanel: React.FC<Props> = ({ stepId, batchId, initialCaseId }) => {
  const [cases, setCases] = useState<CaseStudy[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(initialCaseId);
  const [responses, setResponses] = useState<CaseResponse[]>([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [active, setActive] = useState<CaseResponse | null>(null);
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Selectable cases: step-scoped when we arrived from a step, else the library.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCases(true);
      try {
        const resp = stepId
          ? await CaseStudyService.listForStep(stepId)
          : await CaseStudyService.list();
        if (cancelled) return;
        const list = resp.data.cases || [];
        setCases(list);
        setSelectedCaseId((prev) => prev ?? (list.length ? list[0]!.Id : null));
      } catch {
        if (!cancelled) setError("Could not load case studies.");
      } finally {
        if (!cancelled) setLoadingCases(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stepId]);

  useEffect(() => {
    if (!selectedCaseId) {
      setResponses([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await CaseStudyService.listResponses(selectedCaseId, {
          ...(stepId ? { stepId } : {}),
          ...(batchId ? { batchId } : {}),
        });
        if (!cancelled) setResponses(resp.data.responses || []);
      } catch {
        if (!cancelled) setError("Could not load responses.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCaseId, stepId, batchId]);

  const openCorrection = (r: CaseResponse) => {
    setActive(r);
    setFeedback(r.teacherFeedback || "");
    setScore(r.score != null ? String(r.score) : "");
  };

  const saveCorrection = async () => {
    if (!active) return;
    setSaving(true);
    setError(null);
    try {
      await CaseStudyService.evaluateResponse(active.responseId, {
        feedback,
        score: score === "" ? null : Number(score),
      });
      // Reflect locally so the table updates without a full refetch.
      setResponses((rows) =>
        rows.map((row) =>
          row.responseId === active.responseId
            ? {
                ...row,
                status: "evaluated",
                teacherFeedback: feedback,
                score: score === "" ? null : Number(score),
              }
            : row,
        ),
      );
      setActive(null);
    } catch {
      setError("Could not save your evaluation.");
    } finally {
      setSaving(false);
    }
  };

  const pending = responses.filter((r) => r.status !== "evaluated").length;

  return (
    <div className="cs-responses">
      <div className="cs-responses-controls">
        <Form.Group className="cs-responses-case">
          <Form.Label className="cs-responses-label">Case</Form.Label>
          <Form.Select
            value={selectedCaseId ?? ""}
            onChange={(e) => setSelectedCaseId(e.target.value ? Number(e.target.value) : null)}
            disabled={loadingCases}
          >
            {!cases.length && <option value="">No case studies</option>}
            {cases.map((c) => (
              <option key={c.Id} value={c.Id}>
                {c.Title}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <div className="cs-responses-summary">
          {responses.length} response{responses.length === 1 ? "" : "s"}
          {pending > 0 && <span className="cs-responses-pending"> · {pending} to correct</span>}
        </div>
      </div>

      {error && <div className="cs-error-card">{error}</div>}

      {loading ? (
        <div className="cs-responses-loading">
          <Spinner animation="border" size="sm" /> Loading responses…
        </div>
      ) : responses.length === 0 ? (
        <div className="cs-responses-empty">No student responses yet for this case.</div>
      ) : (
        <div className="table-responsive">
          <Table hover className="cs-responses-table">
            <thead>
              <tr>
                <th>Student / Group</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Score</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {responses.map((r) => (
                <tr key={r.responseId}>
                  <td>
                    {r.groupId ? (
                      <>
                        {r.groupName || `Group #${r.groupId}`}
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "1px 7px",
                            borderRadius: 999,
                            background: "#faf5ff",
                            color: "var(--brand-accent)",
                          }}
                        >
                          Group
                        </span>
                      </>
                    ) : (
                      r.studentName || `User #${r.userId}`
                    )}
                  </td>
                  <td>{formatDate(r.submittedAt)}</td>
                  <td>
                    <StatusPill status={r.status} />
                  </td>
                  <td>{r.score != null ? r.score : "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <Button
                      variant={r.status === "evaluated" ? "secondary" : "primary"}
                      size="sm"
                      onClick={() => openCorrection(r)}
                    >
                      {r.status === "evaluated" ? "Review" : "Correct"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={!!active} onHide={() => !saving && setActive(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Correct response —{" "}
            {active?.groupId
              ? active?.groupName || `Group #${active?.groupId}`
              : active?.studentName || `User #${active?.userId}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="cs-corr-response-label">Student's response</div>
          <div className="cs-corr-response">{active?.responseText}</div>
          <Form.Group className="cs-corr-field">
            <Form.Label>Feedback</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Explain what's strong and what to improve…"
            />
          </Form.Group>
          <Form.Group className="cs-corr-field">
            <Form.Label>Score (optional)</Form.Label>
            <Form.Control
              type="number"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              placeholder="e.g. 8"
              style={{ maxWidth: 120 }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActive(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveCorrection} disabled={saving}>
            {saving ? "Saving…" : "Save correction"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CaseResponsesPanel;
